/**
 * Shared search-param validators + types used across routes that participate
 * in the channel switcher / pagination conventions. Centralizing them avoids
 * three different validators each handling `?channel=` slightly differently.
 */

/** Routes whose only persistent query state is the active channel slug. */
export const validateChannelOnly = (raw: Record<string, unknown>) => {
  const out: { channel?: string } = {}
  if (typeof raw['channel'] === 'string' && raw['channel']) out.channel = raw['channel']
  return out
}

/** Plataformas cujo callback de OAuth volta para ``/channels``. */
export const OAUTH_PLATFORMS = ['youtube', 'tiktok', 'instagram'] as const
export type OAuthCallbackPlatform = (typeof OAUTH_PLATFORMS)[number]

/** Um código de resultado do callback: palavra curta em snake_case, nada além. */
const CALLBACK_CODE = /^[a-z][a-z0-9_]{0,39}$/

/**
 * ``/channels``: o canal ativo e, na volta de um OAuth, o desfecho. O
 * Instagram manda ``platform`` e ``result`` (``connected``, ``denied``…);
 * YouTube e TikTok, só ``error`` quando falham. A página mostra a mensagem e
 * tira os três da URL. Valor fora do formato é descartado: o texto da tela
 * vem de uma lista fechada, nunca da URL.
 */
export interface ChannelsSearch {
  channel?: string
  platform?: OAuthCallbackPlatform
  result?: string
  error?: string
}

export function validateChannelsSearch(raw: Record<string, unknown>): ChannelsSearch {
  const out: ChannelsSearch = validateChannelOnly(raw)
  const platform = raw['platform']
  if (typeof platform === 'string' && (OAUTH_PLATFORMS as readonly string[]).includes(platform)) {
    out.platform = platform as OAuthCallbackPlatform
  }
  const result = raw['result']
  if (typeof result === 'string' && CALLBACK_CODE.test(result)) out.result = result
  const error = raw['error']
  if (typeof error === 'string' && CALLBACK_CODE.test(error)) out.error = error
  return out
}

/**
 * Clip creator. The open project rides in the URL so a reload — or the
 * browser's back button — lands back in the editor instead of dumping the
 * user on the project list. The id is a localStorage key, not a server
 * resource: an unknown one just falls through to the list.
 */
export interface ClipCreatorSearch {
  channel?: string
  project?: string
}

export function validateClipCreatorSearch(raw: Record<string, unknown>): ClipCreatorSearch {
  const out: ClipCreatorSearch = validateChannelOnly(raw)
  if (typeof raw['project'] === 'string' && raw['project']) out.project = raw['project']
  return out
}

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const

/**
 * Search shape for paginated table routes (ranking, analytics). `channel`
 * tags along so the sidebar switcher works uniformly. `factory` is
 * ranking-only but kept here because the sanitizer is shared and the
 * analytics page just ignores it.
 */
export interface PaginatedSearch {
  channel?: string
  page?: number
  size?: number
  factory?: string
}

export function validatePaginatedSearch(raw: Record<string, unknown>): PaginatedSearch {
  const out: PaginatedSearch = {}
  if (typeof raw['channel'] === 'string' && raw['channel']) out.channel = raw['channel']
  // page: positive int; clamp at 1 floor (Pagination component handles overflow).
  const rawPage = raw['page']
  const pageNum = typeof rawPage === 'number' ? rawPage : Number(rawPage)
  if (Number.isFinite(pageNum) && pageNum > 1) out.page = Math.floor(pageNum)
  // size: must be one of the allowed page sizes — drop garbage to default.
  const rawSize = raw['size']
  const sizeNum = typeof rawSize === 'number' ? rawSize : Number(rawSize)
  if ((PAGE_SIZE_OPTIONS as readonly number[]).includes(sizeNum)) out.size = sizeNum
  if (typeof raw['factory'] === 'string' && raw['factory']) out.factory = raw['factory']
  return out
}

/**
 * Analytics tabs — kept in the URL so refresh / shareable links survive.
 * Whitelist used by the validator; the page falls back to the first
 * value when a stale link arrives.
 */
export const ANALYTICS_TABS = ['hours', 'correlation', 'uploads', 'retention', 'compare'] as const
export type AnalyticsTab = (typeof ANALYTICS_TABS)[number]

export interface AnalyticsSearch extends PaginatedSearch {
  tab?: AnalyticsTab
}

/**
 * Configurações tabs — in the URL for the same reason as analytics, and so
 * the old /notifications path can redirect straight to its tab.
 */
export const SETTINGS_TABS = ['generation', 'videos', 'notifications', 'disk', 'device'] as const
export type SettingsTab = (typeof SETTINGS_TABS)[number]

export interface SettingsSearch {
  channel?: string
  tab?: SettingsTab
}

export function validateSettingsSearch(raw: Record<string, unknown>): SettingsSearch {
  const out: SettingsSearch = validateChannelOnly(raw)
  const rawTab = raw['tab']
  if (typeof rawTab === 'string' && (SETTINGS_TABS as readonly string[]).includes(rawTab)) {
    out.tab = rawTab as SettingsTab
  }
  return out
}

/**
 * Project detail tabs — "Informações" plus the former right-hand docks.
 * In the URL so a reload lands on the same tab.
 */
export const PROJECT_TABS = [
  'info',
  'ranking',
  'script',
  'narration',
  'captions',
  'thumb',
  'safety',
  'youtube',
] as const
export type ProjectTab = (typeof PROJECT_TABS)[number]

export function isProjectTab(value: unknown): value is ProjectTab {
  return typeof value === 'string' && (PROJECT_TABS as readonly string[]).includes(value)
}

/** Nomes antigos que links salvos ainda trazem: a aba "Analytics" virou
 *  "YouTube" em 28/10/2026 (docs/plan-youtube-visibility.md). */
const LEGACY_PROJECT_TABS: ReadonlyMap<string, ProjectTab> = new Map([['analytics', 'youtube']])

/** A aba do ``?tab=``: as atuais passam, as antigas viram a nova, o resto cai. */
export function parseProjectTab(value: unknown): ProjectTab | undefined {
  if (isProjectTab(value)) return value
  return typeof value === 'string' ? LEGACY_PROJECT_TABS.get(value) : undefined
}

export function validateAnalyticsSearch(raw: Record<string, unknown>): AnalyticsSearch {
  const out: AnalyticsSearch = validatePaginatedSearch(raw)
  const rawTab = raw['tab']
  if (typeof rawTab === 'string' && (ANALYTICS_TABS as readonly string[]).includes(rawTab)) {
    out.tab = rawTab as AnalyticsTab
  }
  return out
}
